import React from 'react';
import { Line } from '@ant-design/charts';
import { Card, Col, Row, Statistic, Table, Typography, Flex, Switch } from 'antd';
import RangePicker from 'components/date-range-picker';
import { formatCurrency, formatNumber } from 'utils/number';
import { formatDate } from 'utils/date';

const { Text } = Typography;

const Desktop = ({ viewModel }) => {
  const { summary, data, onDateRangeFilterChange, onSortByChange, rangePickerPresets, rangePickerValue, loading, filter } = viewModel;

  const topProductColumns = [
    {
      title: 'Sản phẩm',
      render: (_, record) => `${record?.product?.name || 'Sản phẩm'} ${record?.variant?.variant_title || ''}`.trim(),
    },
    {
      title: 'Số lượng',
      dataIndex: 'quantity',
      render: (quantity) => formatNumber(quantity),
    },
    {
      title: 'Doanh số',
      dataIndex: 'revenue',
      render: (revenue) => formatCurrency(revenue || 0),
      align: 'right',
    },
  ];

  const orderColumns = [
    {
      title: 'Đơn hàng',
      render: (_, record) => (
        <div>
          <div>{record.id}</div>
          <Text type='secondary'>{formatDate(record.create_on)}</Text>
        </div>
      ),
    },
    {
      title: 'Khách hàng',
      render: (_, record) => record?.customer?.full_name || '-'
    },
    {
      title: 'Thành tiền',
      render: (_, record) => formatCurrency(record.total_amount),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      render: (status) => {
        const statusMap = {
          pending: 'Chờ xác nhận',
          confirmed: 'Đã xác nhận',
          processing: 'Đang xử lý',
          shipped: 'Đã gửi',
          delivered: 'Đã giao',
          canceled: 'Đã hủy',
        };
        return statusMap[status] || status;
      },
    },
  ];

  const salesByDay = Array.isArray(data.salesByDay)
    ? data.salesByDay.map((item) => ({
        date: formatDate(item.date),
        'Doanh số': Number(item.revenue) || 0,
      }))
    : [];

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Flex justify='space-between' align='center'>
          <div>
            <Typography.Title level={4} style={{ marginBottom: 0 }}>Báo cáo bán hàng</Typography.Title>
            <Text type='secondary'>Theo dõi doanh thu và sản phẩm bán chạy của shop</Text>
          </div>
          <RangePicker format='DD/MM/YY' presets={rangePickerPresets} value={rangePickerValue} onChange={onDateRangeFilterChange} />
        </Flex>
      </Col>

      <Col span={6}><Card loading={loading}><Statistic title='Doanh thu' value={formatCurrency(summary.revenue || 0)} /></Card></Col>
      <Col span={6}><Card loading={loading}><Statistic title='Số đơn' value={summary.order_count || 0} /></Card></Col>
      <Col span={6}><Card loading={loading}><Statistic title='Giá trị đơn TB' value={formatCurrency(summary.average_order_value || 0)} /></Card></Col>
      <Col span={6}><Card loading={loading}><Statistic title='Khách hàng' value={summary.customer_count || 0} /></Card></Col>

      <Col span={24}>
        <Card title='Xu hướng doanh số theo ngày' loading={loading}>
          <Line
            data={salesByDay}
            xField='date'
            yField='Doanh số'
            height={300}
            point={{ size: 4 }}
            smooth
            yAxis={{
              label: {
                formatter: (v) => formatCurrency(v),
              },
            }}
            tooltip={{
              title: (datum) => datum?.date,
              formatter: (datum) => {
                return {
                  name: 'Doanh số',
                  value: formatCurrency(datum?.['Doanh số'] ?? 0),
                };
              },
            }}
          />
        </Card>
      </Col>

      <Col span={12}>
        <Card
          title='Top sản phẩm bán chạy'
          loading={loading}
          extra={
            <Flex gap={8} align='center'>
              <Text style={{ fontSize: 12 }}>Số lượng</Text>
              <Switch
                checked={filter.sortBy === 'revenue'}
                onChange={(checked) => onSortByChange(checked ? 'revenue' : 'quantity')}
              />
              <Text style={{ fontSize: 12 }}>Doanh số</Text>
            </Flex>
          }
        >
          <Table rowKey={(record) => record.id} pagination={false} columns={topProductColumns} dataSource={data.topProducts || []} />
        </Card>
      </Col>

      <Col span={12}>
        <Card title='Đơn hàng gần đây' loading={loading}>
          <Table rowKey={(record) => record.id} pagination={false} columns={orderColumns} dataSource={data.orders || []} />
        </Card>
      </Col>
    </Row>
  );
};

export default Desktop;
